import React, { FC, Suspense, useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { CreditPackRate, CreditPackSelector } from 'src/components/pages/myAccount/creditPackSelector';
import { ActionsSection, AutoRefillSection, ServiceCreditsContent } from 'src/components/pages/myAccount/serviceCreditsComponents';
import { AiTextButton } from 'src/components/widgets/ai';
import { Checkbox } from 'src/components/widgets/checkbox';
import { Dialog, useDialogManager } from 'src/components/widgets/dialog';
import Loader from 'src/components/widgets/loader';
import { showNotification } from 'src/components/widgets/notificationToaster';
import { PrimaryButtonWrapper } from 'src/components/widgets/section/sectionFooter';
import { Spinner } from 'src/components/widgets/spinner';
import { Center } from 'src/shared/globals';
import { backofficeUrl, T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';

import { discoveryAiCreditsPurchaseDialogBuyMutation, discoveryAiCreditsPurchaseDialogBuyMutation$data } from './__generated__/discoveryAiCreditsPurchaseDialogBuyMutation.graphql';
import { discoveryAiCreditsPurchaseDialogQuery } from './__generated__/discoveryAiCreditsPurchaseDialogQuery.graphql';

// AI Creative Tools all draw from the shared GENERATIVE_AI pool; its packs are priced in USD.
const GENERATIVE_AI = 'GENERATIVE_AI' as const;
const ADDON_CURRENCY_CODE = 'USD';

interface DiscoveryAiCreditsPurchaseDialogProps {
    /** Fired after a successful purchase that did not require an external approval redirect. */
    onPurchased?: () => void;
    /** Fired when the dialog is dismissed without purchasing (close button / overlay). */
    onClose?: () => void;
    /** Base URL the Shopify approval flow should return to (e.g. the current test product page). */
    returnUrlAfterApproval?: string;
}

interface PurchaseContentProps {
    isShopify: boolean;
    subscriptionCurrencyCode: string;
    returnUrlAfterApproval?: string;
    onPurchased: () => void;
}

// Inner (Suspense-boundaried) content: fetches the GENERATIVE_AI packs + exchange rates, then lets the seller
// pick a pack and buy it. Kept separate so the enclosing Dialog can render a spinner while Relay loads.
const PurchaseContent: FC<PurchaseContentProps> = ({ isShopify, subscriptionCurrencyCode, returnUrlAfterApproval, onPurchased }) => {
    const data = useLazyLoadQuery<discoveryAiCreditsPurchaseDialogQuery>(
        graphql`
            query discoveryAiCreditsPurchaseDialogQuery($addonKey: String!, $currencyCode: String!, $baseCurrencyCode: String!) {
                addon(addonKey: $addonKey, currencyCode: $currencyCode) {
                    rates {
                        feeValue
                        usageFrom
                        rateID
                    }
                }
                exchangeRates(baseCurrencyCode: $baseCurrencyCode) {
                    rates {
                        code
                        rate
                        zeroDecimal
                    }
                }
            }
        `,
        { addonKey: GENERATIVE_AI, currencyCode: ADDON_CURRENCY_CODE, baseCurrencyCode: ADDON_CURRENCY_CODE },
        { fetchPolicy: 'store-or-network' }
    );

    const [buyCredits] = useMutation<discoveryAiCreditsPurchaseDialogBuyMutation>(graphql`
        mutation discoveryAiCreditsPurchaseDialogBuyMutation($input: ServiceRechargeCreditsInput!) {
            buyServiceCredits(input: $input) {
                errorMessage
                result
                urlForApproval
            }
        }
    `);

    const rates = (data.addon?.rates ?? []) as CreditPackRate[];

    // Same currency conversion the My Account credits page uses (feeValue is in the addon's base currency).
    const currencyRateForPrice =
        subscriptionCurrencyCode === 'EUR' || subscriptionCurrencyCode === 'USD' || subscriptionCurrencyCode === 'GBP'
            ? 1
            : (data.exchangeRates.rates?.find((rate) => rate?.code === subscriptionCurrencyCode)?.rate ?? 1);
    const hasCurrencyDecimals = !data.exchangeRates.rates?.find((rate) => rate?.code === subscriptionCurrencyCode)?.zeroDecimal;
    const formatPrice = (value: number) =>
        `${(value * currencyRateForPrice).toFixed(hasCurrencyDecimals ? 2 : 0).replace('.', ',')} ${subscriptionCurrencyCode}`;

    const [selectedTier, setSelectedTier] = useState<number | null>(rates[0]?.rateID ?? null);
    // Discovery never auto-refills by default — the seller can still opt in for their own account.
    const [autoRefill, setAutoRefill] = useState<boolean>(false);
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const handleBuy = () => {
        const selectedRate = rates.find((r) => r.rateID === selectedTier);
        if (!selectedRate) {
            showNotification({ type: 'warning', message: T._('Please select a package before purchasing credits.', 'Admin'), dismiss: { duration: 2000 } });
            return;
        }
        setIsProcessing(true);

        const url = new URL(window.location.href);
        const returnBase = returnUrlAfterApproval ?? `${url.protocol}//${url.host}${backofficeUrl('/User/?tab=subscriptions')}`;
        const redirectUrlFromApproval = `${returnBase}${returnBase.includes('?') ? '&' : '?'}for${GENERATIVE_AI}=true`;

        buyCredits({
            variables: {
                input: {
                    service: GENERATIVE_AI,
                    shopifyChargeID: null,
                    credits: selectedRate.usageFrom,
                    autoRefill,
                    currencyCode: ADDON_CURRENCY_CODE,
                    isForTrial: false,
                    returnUrlFromApproval: isShopify ? redirectUrlFromApproval : null
                }
            },
            onCompleted: (response: discoveryAiCreditsPurchaseDialogBuyMutation$data) => {
                const result = response.buyServiceCredits;
                if (result && result.result !== 'FAILED') {
                    if (result.urlForApproval) {
                        // Shopify: bounce to the charge-approval page; the return URL finalizes the purchase.
                        window.location.href = result.urlForApproval;
                        return;
                    }
                    setIsProcessing(false);
                    showNotification({
                        type: 'success',
                        message: T._('Credits added. You can now use AI Creative Tools.', 'Admin'),
                        dismiss: { duration: 8000 }
                    });
                    onPurchased();
                } else {
                    setIsProcessing(false);
                    showNotification({
                        type: 'danger',
                        message: result?.errorMessage || T._('An error occurred while processing your request.', 'Admin'),
                        dismiss: { duration: 3000 }
                    });
                }
            },
            onError: () => {
                setIsProcessing(false);
                showNotification({ type: 'danger', message: T._('An error occurred while purchasing credits.', 'Admin'), dismiss: { duration: 2000 } });
            }
        });
    };

    return (
        <ServiceCreditsContent>
            <p>
                {T._(
                    "You've used all your AI credits. Purchase a credit pack to keep using the AI Creative Tools in the customizer.",
                    'Admin'
                )}
            </p>
            <CreditPackSelector rates={rates} formatPrice={formatPrice} selectedTier={selectedTier} onSelectTier={setSelectedTier} disabled={isProcessing} />
            <AutoRefillSection>
                <Checkbox
                    checked={autoRefill}
                    onChange={setAutoRefill}
                    label={T._('Enable auto-refill', 'Admin')}
                    caption={T._('Automatically purchase the same credit package when your balance runs low to avoid service interruption.', 'Admin')}
                    disabled={isProcessing}
                />
            </AutoRefillSection>
            <ActionsSection>
                {isProcessing ? (
                    <Center>
                        <Spinner />
                    </Center>
                ) : (
                    <PrimaryButtonWrapper>
                        <AiTextButton disabled={!selectedTier || isProcessing} onClick={handleBuy}>
                            {T._('Buy credits', 'Admin')}
                        </AiTextButton>
                    </PrimaryButtonWrapper>
                )}
            </ActionsSection>
        </ServiceCreditsContent>
    );
};

/**
 * Discovery-mode "buy AI credits" dialog (CASE 3: the seller ran out). A focused purchase modal that renders
 * the shared {@link CreditPackSelector} and runs its own `buyServiceCredits` with auto-refill defaulted OFF —
 * unlike the My Account credits page, discovery must never enable auto-refill implicitly.
 */
const DiscoveryAiCreditsPurchaseDialog: FC<DiscoveryAiCreditsPurchaseDialogProps> = ({ onPurchased, onClose, returnUrlAfterApproval }) => {
    const { closeDialog, currentDialogId } = useDialogManager();
    const { subscription, ecommerceName, salesChannels } = useSubscriptionData();

    const isShopify = ecommerceName?.toUpperCase() === 'SHOPIFY';
    const subscriptionCurrencyCode = isShopify
        ? (salesChannels.at(0)?.ecommerceSettings?.currencyCode ?? 'USD')
        : (subscription?.plan?.currencyCode ?? 'USD');

    const handleClose = () => {
        onClose?.();
        closeDialog(currentDialogId);
    };

    const handlePurchased = () => {
        onPurchased?.();
        closeDialog(currentDialogId);
    };

    return (
        <Dialog title={T._('Buy AI credits', 'Admin')} onClose={handleClose} showCloseButton>
            <Suspense
                fallback={
                    <Center>
                        <Loader />
                    </Center>
                }
            >
                <PurchaseContent
                    isShopify={isShopify}
                    subscriptionCurrencyCode={subscriptionCurrencyCode}
                    returnUrlAfterApproval={returnUrlAfterApproval}
                    onPurchased={handlePurchased}
                />
            </Suspense>
        </Dialog>
    );
};

export default DiscoveryAiCreditsPurchaseDialog;
